import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import Tooltip from '@mui/material/Tooltip';
import {
  OVERFLOW_TOOLTIP_ENTER_DELAY_MS,
  useOverflowTitle,
} from '@components/Common/OverflowTooltip';
import React, { useMemo, useState } from 'react';
import './index.scss';

export type MetricAlign = 'left' | 'right' | 'center';
export type MetricVariant = 'rank' | 'name' | 'strong';

export interface MetricColumn<T> {
  /** Stable key; also the sort identity. */
  key: string;
  /** Header content (string or node). */
  header: React.ReactNode;
  /** Optional header hover explanation. */
  tooltip?: string;
  /** Cell alignment. Defaults from variant: name -> left, rank -> center, else right. */
  align?: MetricAlign;
  /** Styling hook: rank (#), name (pinned identity col), strong (emphasis KPI). */
  variant?: MetricVariant;
  /** Cell renderer. */
  render: (row: T, index: number) => React.ReactNode;
  /** Provide to make the column click-to-sort. */
  sortValue?: (row: T) => number | string;
  /** Server-sorted tables (`onSortChange`): this column's header asks the server to sort by it. */
  serverSort?: boolean;
}

export interface MetricTableProps<T> {
  columns: MetricColumn<T>[];
  rows: T[];
  getRowKey?: (row: T, index: number) => string;
  emptyLabel?: string;
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  /**
   * Pin the name column at every width, not only below the laptop floor. For a table that is
   * wider than a desktop by design, such as one column per week for half a year.
   */
  pinName?: boolean;
  /**
   * Server-side sorting, for a paged table where only the server can order the whole set. The
   * table shows `sort` and reports header clicks on `serverSort` columns instead of reordering
   * the rows it has, which would sort one page and pass it off as the ranking.
   */
  sort?: { key: string; dir: 'asc' | 'desc' };
  onSortChange?: (key: string) => void;
}

const alignOf = <T,>(col: MetricColumn<T>): MetricAlign =>
  col.align ?? (col.variant === 'name' ? 'left' : col.variant === 'rank' ? 'center' : 'right');

/**
 * A body cell that reveals its full text when the column cut it off.
 *
 * The name column is capped at 240px and ellipsised, which is exactly where a job title or a
 * company name stops being readable. Its own component because the measurement is a hook and
 * these are rendered in a map.
 */
const MetricCell: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children,
}) => {
  const { ref, title, onMouseEnter, onFocus } = useOverflowTitle<HTMLTableCellElement>();

  return (
    <Tooltip disableInteractive enterDelay={OVERFLOW_TOOLTIP_ENTER_DELAY_MS} title={title}>
      <td className={className} ref={ref} onFocus={onFocus} onMouseEnter={onMouseEnter}>
        {children}
      </td>
    </Tooltip>
  );
};

/**
 * Dense numeric analytics table: right-aligned tabular figures, sticky header, a pinned
 * first (name) column with horizontal scroll on narrow viewports (no card-collapse), header
 * tooltips, and client-side click-to-sort. Purpose-built for the dashboard comparison tables;
 * for identity-first entity lists use the app-wide MintlyTable (Common/Table) instead.
 */
function MetricTable<T>({
  columns,
  rows,
  getRowKey,
  emptyLabel = 'No data',
  defaultSort,
  pinName = false,
  sort: serverSortState,
  onSortChange,
}: MetricTableProps<T>) {
  const [localSort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(
    defaultSort ?? null,
  );
  const serverMode = !!onSortChange;
  const sort = serverMode ? (serverSortState ?? null) : localSort;
  const isSortable = (col: MetricColumn<T>) =>
    serverMode ? !!col.serverSort : !!col.sortValue;

  const sortedRows = useMemo(() => {
    if (serverMode || !sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Copy so we never mutate the caller's array.
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, columns, sort, serverMode]);

  const onHeaderClick = (col: MetricColumn<T>) => {
    if (!isSortable(col)) return;
    if (serverMode) {
      onSortChange!(col.key);
      return;
    }
    setSort((prev) =>
      prev?.key === col.key
        ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: 'desc' }, // first click on a metric shows the leaders
    );
  };

  return (
    <div className="mt-scroll">
      <table className={pinName ? 'mt-table mt-table--pin-name' : 'mt-table'}>
        <thead>
          <tr>
            {columns.map((col) => {
              const align = alignOf(col);
              const sortable = isSortable(col);
              const active = sort?.key === col.key;
              const head = (
                <span className="mt-th-label">
                  {col.header}
                  {sortable && active && (
                    sort!.dir === 'asc' ? (
                      <ArrowUpwardRoundedIcon className="mt-sort-icon" />
                    ) : (
                      <ArrowDownwardRoundedIcon className="mt-sort-icon" />
                    )
                  )}
                </span>
              );
              return (
                <th
                  key={col.key}
                  className={[
                    'mt-th',
                    `mt-align-${align}`,
                    col.variant ? `mt-th--${col.variant}` : '',
                    sortable ? 'mt-th--sortable' : '',
                    active ? 'mt-th--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  onClick={sortable ? () => onHeaderClick(col) : undefined}
                >
                  {col.tooltip ? (
                    <Tooltip title={col.tooltip} arrow>
                      {head}
                    </Tooltip>
                  ) : (
                    head
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td className="mt-empty" colSpan={columns.length}>
                {emptyLabel}
              </td>
            </tr>
          ) : (
            sortedRows.map((row, i) => (
              <tr key={getRowKey ? getRowKey(row, i) : i} className="mt-row">
                {columns.map((col) => {
                  const align = alignOf(col);
                  return (
                    <MetricCell
                      key={col.key}
                      className={[
                        'mt-td',
                        `mt-align-${align}`,
                        col.variant ? `mt-td--${col.variant}` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {col.render(row, i)}
                    </MetricCell>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default MetricTable;
